import { DataTable, FormInput, FormLabel, FormSectionHeader, FormSelect, Heading, Loader } from "@/components";
import { Button, Card, CardContent, Checkbox, Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui";
import useOwnersReport from "./useOwnersReport";
import { FormProvider } from "react-hook-form";
import { BLOCK, COMMUNITY, OCCUPANCY, occupancyOptions, SEARCH, tabs, ZONE } from "./consts";
import { allOption, cn } from "@/lib/utils";
import { Download } from "lucide-react";
import { OutstandingDueDialog } from "./OutstandingDueDialog";
import { CommunityDialog } from "./CommunityDialog";
import { PropertyDialog } from "./PropertyDialog";

const OwnersReport = () => {
  const { form, isLoadingCommunity, isLoadingZoneList,
    isBlockLoading, communityList, zoneList, propertyBlock,
    activeTab, onTabChange, isMobile, columns, onOutstandingCheckedChange,
    outstandingDues, onClearAll, onSubmit, isLoadingReport, reportData, pageNumber,
    setPageNumber, pageSize, setPageSize, sorting, setSorting, openCommunityDialog, setOpenCommunityDialog,
    openOutstandingDialog, setOpenOutstandingDialog, selectedRow, openPropertyDialog,
    setOpenPropertyDialog, formData, onExport, isLoadingExportReport
   } = useOwnersReport();

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between mb-6">
        <Heading
          title="Owners Report"
        />
      </div>

      <Card className="shadow-none rounded-md">
        <CardContent>
          <FormSectionHeader>Find Owners</FormSectionHeader>

          {isLoadingCommunity ? <Loader /> : 
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[20px]">
                  <FormSelect
                    multiSelect
                    name={COMMUNITY}
                    control={form.control}
                    label="Community"
                    placeholder="Please select"
                    options={[...allOption, ...communityList?.map((data: any) => ({
                      value: data?.communityId?.toString(),
                      label: data?.name,
                    }))]}
                  />
                  <FormSelect
                    multiSelect
                    name={ZONE}
                    control={form.control}
                    label="Zone"
                    placeholder="Please select"
                    options={[...allOption, ...zoneList
                      ?.filter((data: any) => formData[COMMUNITY] == "all" || formData[COMMUNITY]?.includes(data.communityId?.toString()))
                      ?.map((data: any) => ({
                      value: data?.zoneId?.toString(),
                      label: data?.zoneName,
                    }))]}
                    disabled={isLoadingZoneList}
                  />
                  <FormSelect
                    multiSelect
                    name={BLOCK}
                    control={form.control}
                    label="Block"
                    placeholder="Please select"
                    options={[...allOption, ...propertyBlock
                      ?.filter((data: any) => formData[ZONE] == "all" || formData[ZONE]?.includes(data.zoneId?.toString()))
                      ?.map((data: any) => ({
                      value: data?.blockId?.toString(),
                      label: data?.blockName,
                    }))]}
                    disabled={isBlockLoading}
                  />
                  <FormSelect
                    multiSelect
                    name={OCCUPANCY}
                    control={form.control}
                    label="Occupancy"
                    placeholder="Please select"
                    options={[...allOption, ...occupancyOptions]}
                  />
                  <div className="lg:col-span-2">
                    <FormInput
                      name={SEARCH}
                      control={form.control}
                      label="Owner Search"
                      placeholder="Please enter"
                    />
                  </div>
                  <div className="lg:col-span-2 flex flex-row gap-3 lg:items-center lg:justify-end">
                    <Button>Search</Button>
                    <Button variant="outline" type="button" onClick={onClearAll}>Clear All</Button>
                  </div>
                </div>
              </form>
            </FormProvider>
          }
        </CardContent>
      </Card>

      <Tabs
        value={activeTab}
        onValueChange={onTabChange}
        className="w-full mt-5"
      >
        <div className="flex flex-row justify-between">
          <TabsList
            className={cn(
              "gap-x-4",
              isMobile ? "!w-full !overflow-x-scroll" : ""
            )}
          >
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="data-[state=active]:shadow-none hover:cursor-pointer text-gray-400 hover:text-primary px-0 border-0 shadow-none data-[state=active]:rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-primary"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="flex flex-row items-center gap-3">
            <div className="flex flex-row items-center">
              <Checkbox
                className="mr-1 bg-white"
                onCheckedChange={onOutstandingCheckedChange}
                checked={outstandingDues}
              />
              <FormLabel isCheckbox className="bg-transparent">Outstanding dues only</FormLabel>
            </div>
            <Button className="bg-green-700 hover:bg-green-700" type="button" onClick={onExport} disabled={isLoadingExportReport}>
              {isLoadingExportReport ? <Loader variant="secondary" /> : <Download />} Bulk Download
            </Button>
          </div>
        </div>

        <TabsContent value={activeTab} className="w-full bg-white">
          {isLoadingReport ? <Loader /> : 
            <DataTable
              data={reportData?.data || []}
              columns={columns}
              pageSize={pageSize}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setPageSize={setPageSize}
              totalPages={Math.ceil(reportData?.total / pageSize)}
              totalRecords={reportData?.total}
              serverSidePagination={true}
              sorting={sorting}
              onSortingChange={setSorting}
            />
          }
        </TabsContent>
      </Tabs>
      {openOutstandingDialog &&
        <OutstandingDueDialog
          open={openOutstandingDialog}
          setOpen={setOpenOutstandingDialog}
          selectedRow={selectedRow}
        />}
      {openCommunityDialog &&
        <CommunityDialog
          open={openCommunityDialog}
          setOpen={setOpenCommunityDialog}
          selectedRow={selectedRow}
        />}
      {openPropertyDialog &&
        <PropertyDialog
          open={openPropertyDialog}
          setOpen={setOpenPropertyDialog}
          selectedRow={selectedRow}
        />}
    </div>
  )
}

export default OwnersReport;